import React, { useState, useEffect, useRef } from 'react';
import TrebbleModal from './TrebbleModal';
import TrebbleInput from './TrebbleInput';
import TrebbleButton from './TrebbleButton';
import { TrebbleTitle } from './TrebbleTypography';

interface FolderNameModalProps {
  open: boolean;
  title?: string;
  placeholder?: string;
  okText?: string;
  cancelText?: string;
  initialValue?: string;
  onConfirm: (name: string) => Promise<void> | void;
  onCancel: () => void;
  useEditorTheme?: boolean;
  ti18n?: unknown;
}

/**
 * FolderNameModalContent renders just the inner JSX of the folder-name modal
 * (title + input + footer buttons). Use this when the caller already provides
 * its own modal/popup wrapper (e.g., OptionPopupView) so two TrebbleModal
 * wrappers are not stacked.
 */
export interface FolderNameModalContentProps {
  title?: string;
  placeholder?: string;
  okText?: string;
  cancelText?: string;
  initialValue?: string;
  onConfirm: (name: string) => Promise<void> | void;
  onCancel: () => void;
  useEditorTheme?: boolean;
  ti18n?: unknown;
}

export const FolderNameModalContent: React.FC<FolderNameModalContentProps> = ({
  title,
  placeholder,
  okText,
  cancelText,
  initialValue = '',
  onConfirm,
  onCancel,
  useEditorTheme = false,
  ti18n,
}) => {
  const resolvedTitle = title ?? getI18n(ti18n, 'FOLDER_NEW_FOLDER', 'New Folder');
  const resolvedPlaceholder = placeholder ?? getI18n(ti18n, 'FOLDER_NAME_PLACEHOLDER', 'Folder name');
  const resolvedOkText = okText ?? getI18n(ti18n, 'FOLDER_CREATE', 'Create');
  const resolvedCancelText = cancelText ?? getI18n(ti18n, 'CANCEL', 'Cancel');

  const [name, setName] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setName(initialValue);
    setIsLoading(false);
  }, [initialValue]);

  const handleConfirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsLoading(true);
    try {
      await onConfirm(trimmed);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && name.trim()) handleConfirm();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <>
      <TrebbleTitle
        level={4}
        className="mb-0"
        disableDefaultColors={useEditorTheme}
        style={{ paddingTop: 0, marginTop: 0 }}
      >
        {resolvedTitle}
      </TrebbleTitle>
      <TrebbleInput
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={resolvedPlaceholder}
        disabled={isLoading}
        useEditorTheme={useEditorTheme}
        style={{ marginTop: '8px' }}
      />
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
        <TrebbleButton onClick={onCancel} disabled={isLoading} useEditorTheme={useEditorTheme}>
          {resolvedCancelText}
        </TrebbleButton>
        <TrebbleButton
          type="primary"
          onClick={handleConfirm}
          loading={isLoading}
          disabled={!name.trim() || isLoading}
          useEditorTheme={useEditorTheme}
        >
          {resolvedOkText}
        </TrebbleButton>
      </div>
    </>
  );
};

const getI18n = (ti18n: unknown, key: string, fallback: string): string => {
  if (ti18n && window.getI18n) {
    return window.getI18n(ti18n, key);
  }
  return fallback;
};

const FolderNameModal: React.FC<FolderNameModalProps> = ({
  open,
  title,
  placeholder,
  okText,
  cancelText,
  initialValue = '',
  onConfirm,
  onCancel,
  useEditorTheme = false,
  ti18n,
}) => {
  const resolvedTitle = title ?? getI18n(ti18n, 'FOLDER_NEW_FOLDER', 'New Folder');
  const resolvedPlaceholder = placeholder ?? getI18n(ti18n, 'FOLDER_NAME_PLACEHOLDER', 'Folder name');
  const resolvedOkText = okText ?? getI18n(ti18n, 'FOLDER_CREATE', 'Create');
  const resolvedCancelText = cancelText ?? getI18n(ti18n, 'CANCEL', 'Cancel');

  const [name, setName] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setName(initialValue);
      setIsLoading(false);
    }
  }, [open, initialValue]);

  const handleConfirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setIsLoading(true);
    try {
      await onConfirm(trimmed);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && name.trim()) handleConfirm();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <TrebbleModal
      open={open}
      title={
        <TrebbleTitle
          level={4}
          className="mb-0"
          disableDefaultColors={useEditorTheme}
          style={{ paddingTop: 0, marginTop: 0 }}
        >
          {resolvedTitle}
        </TrebbleTitle>
      }
      onCancel={onCancel}
      centered
      closable={false}
      useEditorTheme={useEditorTheme}
      footer={
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <TrebbleButton onClick={onCancel} disabled={isLoading} useEditorTheme={useEditorTheme}>
            {resolvedCancelText}
          </TrebbleButton>
          <TrebbleButton
            type="primary"
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!name.trim() || isLoading}
            useEditorTheme={useEditorTheme}
          >
            {resolvedOkText}
          </TrebbleButton>
        </div>
      }
    >
      <TrebbleInput
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={resolvedPlaceholder}
        disabled={isLoading}
        useEditorTheme={useEditorTheme}
        style={{ marginTop: '8px' }}
      />
    </TrebbleModal>
  );
};

export default FolderNameModal;
